import { mount, unmount } from "svelte";
import type { Component } from "svelte";
import { setupThemeEditorEvents } from "./bootstrap";
import { sharedQueryClient } from "~/lib/query-client";
import { parseIslandProps } from "./parse-island-props";

/**
 * Factory for page island entrypoints.
 *
 * Page islands have dedicated entrypoints (rather than being registered in the
 * dynamic registry) to avoid the dynamic-import waterfall on page-critical content.
 * This factory collapses the otherwise-identical boilerplate — WeakMap lifecycle
 * management, skeleton removal, theme editor events — into a single call site.
 *
 * @param IslandComponent - The Svelte island component to mount
 * @param islandName - Must match the `data-island="..."` attribute in Liquid
 * @param skeletonSelector - CSS selector for the skeleton placeholder to remove on mount (optional)
 * @param beforeMount - Called before mount; may mutate the container and return extra props.
 *   Runs before skeleton removal so it can read pre-existing DOM. The container's original
 *   child nodes (not a re-parsed copy, so typed values and listeners survive) and className
 *   are automatically restored if mount() subsequently throws.
 */

export function createIslandEntrypoint(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  IslandComponent: Component<any>,
  islandName: string,
  skeletonSelector?: string,
  beforeMount?: (container: HTMLElement) => Record<string, unknown>,
): void {
  const instances = new WeakMap<HTMLElement, ReturnType<typeof mount>>();
  // A failed container is not retried (it would fail the same way and log again), including
  // on a theme-editor reorder: onReorder unmounts and re-mounts the SAME elements and
  // unmountIsland never clears this set, so only a replaced (new) element gets a new try.
  // Trade-off: a failed container is also skipped on the later DOMContentLoaded pass, which
  // used to recover a container caught mid-parse; the aurora tag is a deferred module, so
  // the container is always fully parsed by then.
  const failed = new WeakSet<HTMLElement>();

  function mountIsland(container: HTMLElement): void {
    if (instances.has(container) || failed.has(container)) return;
    const fallbackNodes = Array.from(container.childNodes);
    const fallbackClassName = container.className;
    try {
      const extraProps = beforeMount?.(container) ?? {};
      const props = parseIslandProps(container);
      if (skeletonSelector) container.querySelector(skeletonSelector)?.remove();
      instances.set(
        container,
        mount(IslandComponent, {
          target: container,
          props: { ...props, ...extraProps, name: islandName, queryClient: sharedQueryClient },
        }),
      );
    } catch (error) {
      // Skip when nothing was removed: re-inserting detaches nodes and drops focus/selection.
      const current = container.childNodes;
      const untouched =
        current.length === fallbackNodes.length && fallbackNodes.every((n, i) => current[i] === n);
      if (!untouched) container.replaceChildren(...fallbackNodes);
      container.className = fallbackClassName;
      failed.add(container);
      console.error(`[Aurora] Failed to mount island "${islandName}":`, error);
    }
  }

  function unmountIsland(container: HTMLElement): void {
    const instance = instances.get(container);
    if (instance) {
      try {
        unmount(instance);
        instances.delete(container);
      } catch (error) {
        console.error(`[Aurora] Failed to unmount island "${islandName}":`, error);
      }
    }
  }

  function mountIn(root: HTMLElement | Document): void {
    for (const el of root.querySelectorAll<HTMLElement>(`[data-island="${islandName}"]`)) {
      mountIsland(el);
    }
  }

  function unmountIn(root: HTMLElement | Document): void {
    for (const el of root.querySelectorAll<HTMLElement>(`[data-island="${islandName}"]`)) {
      unmountIsland(el);
    }
  }

  // Synchronous call catches theme-editor live insertions where DOMContentLoaded
  // has already fired. DOMContentLoaded handles the normal initial page load.
  mountIn(document);
  document.addEventListener("DOMContentLoaded", () => mountIn(document));

  setupThemeEditorEvents({
    onLoad: (section) => mountIn(section),
    onUnload: (section) => unmountIn(section),
    onReorder: () => {
      unmountIn(document);
      mountIn(document);
    },
  });
}
